import type { TipTapDoc } from "@compass/shared/schema/tiptapDoc";
import * as Dialog from "@radix-ui/react-dialog";
import { useSnackbar } from "notistack";
import { useRef } from "react";
import { SocialCommentsWrapper } from "screens/social/comment/SocialCommentsWrapper";
import { SocialPostWrapper } from "screens/social/post/SocialPostWrapper";
import { ReactComponent as CloseIcon } from "assets/img/icons/header/close.svg";
import { ThreadCommentForm } from "components/form/ThreadCommentForm";
import { trpc } from "lib/trpc";
import type { ClientPost } from "@compass/shared/types";

type PostModalProps = {
    isOpen: boolean;
    onClose: (open: boolean) => void;
    post: ClientPost;
};
export const PostModal = ({ isOpen, onClose, post }: PostModalProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const scrollableRef = useRef<HTMLDivElement>(null);
    const createCommentMutation = trpc.threads.createComment.useMutation();

    // Get or create thread for this post
    const { data: thread } = trpc.social.posts.getThread.useQuery({
        postId: post.id,
    });

    const queryClient = trpc.useUtils();
    // Use live data instead of static prop when modal is open
    const { data: livePost } = trpc.social.posts.get.useQuery(
        { postId: post.id },
        {
            enabled: isOpen,
            initialData: post,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
        },
    );
    // Use live data when available, fallback to prop
    const currentPost = livePost || post;

    const handleCommentSubmit = (data: {
        contentJson: TipTapDoc;
        attachmentIds: string[];
        isAnonymous: boolean;
    }) => {
        if (createCommentMutation.isPending || !thread) return;

        createCommentMutation.mutate(
            {
                threadId: thread.id,
                contentJson: data.contentJson,
                isPostedAnonymously: data.isAnonymous,
                attachmentIds: data.attachmentIds,
            },
            {
                onSuccess: () => {
                    queryClient.threads.getComments.invalidate();
                    queryClient.social.posts.latest.invalidate();
                    queryClient.social.posts.get.invalidate({ postId: currentPost.id });
                    enqueueSnackbar("Comment created successfully.", { variant: "success" });

                    // Scroll to bottom to show the new comment
                    setTimeout(() => {
                        const el = scrollableRef.current;
                        if (el) {
                            el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
                        }
                    }, 100);
                },
                onError: (e) => {
                    console.error(e);
                    enqueueSnackbar(`${e}`, { variant: "error" });
                },
            },
        );
    };
    return (
        <Dialog.Root open={isOpen} onOpenChange={onClose}>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-[#424242]/75 " />
            <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-white shadow-none focus:outline-none focus:ring-0">
                <div className="flex h-[90vh] max-h-[90vh] flex-col">
                    {/* Header */}
                    <div className="flex shrink-0 items-baseline justify-between px-6 pt-3 pb-3">
                        <h2 className="text-xl font-semibold">
                            Post {currentPost?.author.name ? `by ${currentPost?.author?.name}` : ""}
                        </h2>
                        <div className="flex items-center gap-2">
                            <Dialog.Close asChild>
                                <button className="rounded p-1 hover:bg-gray-100 focus:outline-none focus:ring-0">
                                    <CloseIcon className="h-[30px] w-[30px]" />
                                </button>
                            </Dialog.Close>
                        </div>
                    </div>
                    {/* Scrollable post + comments */}
                    <div ref={scrollableRef} className="flex-1 overflow-y-auto px-6">
                        <div className="mb-4 h-[1px] bg-[#e0e0e0]" />
                        <div className="space-y-2">
                            <SocialPostWrapper post={currentPost as ClientPost} isModalView />
                            {thread && (
                                <SocialCommentsWrapper
                                    post={currentPost as ClientPost}
                                    threadId={thread.id}
                                />
                            )}
                        </div>
                    </div>
                    {/* Fixed comment box */}
                    <div className="mx-6 my-4 shrink-0 rounded border border-[#def1fa] bg-white p-3">
                        <ThreadCommentForm
                            context="social"
                            onSubmit={handleCommentSubmit}
                            isLoading={createCommentMutation.isPending}
                            placeholder="Add a comment..."
                            submitLabel="Add Comment"
                            showUserIdentity
                        />
                    </div>
                </div>
            </Dialog.Content>
        </Dialog.Root>
    );
};
